<template>
  <v-dialog
    v-model="dialogOpen"
    v-if="dialogOpen"
    content-class="add-note-dialog"
    persistent
    scrollable
    max-width="900"
    @keydown.esc="close"
  >
    <v-card class="dialog-card">
      <!-- Header -->
      <v-card-title class="dialog-header pa-4">
        <v-icon color="white" size="28" class="mr-2">mdi-note-text</v-icon>
        <span class="header-text">Napomene</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close" class="close-btn-header">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- SEKCIJA 1: Brza stolna napomena (UVIJEK VIDLJIVA) -->
        <v-card
          outlined
          class="stol-note-card mb-4"
          elevation="2">
          <v-card-title class="stol-note-header py-3">
            <v-icon color="#3989C9" class="mr-2">mdi-note-text</v-icon>
            <span class="text-h6">Napomena za stol</span>
          </v-card-title>
          <v-card-text class="pt-3 pb-3">
            <v-textarea
              ref="stolNoteInput"
              v-model="stolNoteText"
              label="Brza napomena za stol"
              placeholder="npr. Gosti slave rođendan, Bez luka..."
              rows="2"
              auto-grow
              outlined
              dense
              hide-details="auto"
              class="touchscreen-input">
            </v-textarea>
          </v-card-text>
        </v-card>

        <!-- SEKCIJA 2: Napomene za ustrojstvene jedinice (COLLAPSIBLE) -->
        <v-expansion-panels
          v-model="unitNotesPanel"
          class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-header">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-office-building</v-icon>
                <span class="font-weight-bold">Pošalji u ustrojstvene jedinice</span>
                <v-chip x-small class="ml-2">{{ activeUnitNotesCount }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <!-- +/- gumbi za dodavanje/uklanjanje napomena -->
              <v-row class="mb-3" align="center">
                <v-col>
                  <span class="text-caption grey--text">
                    Do {{ maxNotes }} napomene odjednom
                  </span>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    icon
                    large
                    color="error"
                    :disabled="minusDisabled"
                    @click="removeNote">
                    <v-icon>mdi-minus-circle</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    large
                    color="success"
                    :disabled="plusDisabled"
                    @click="addNote">
                    <v-icon>mdi-plus-circle</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Napomene s destinacijama -->
              <div v-for="(note, index) in notes" :key="index" class="mb-4">
                <v-card outlined>
                  <!-- Checkboxes za ustrojstvene jedinice -->
                  <v-card-subtitle class="pb-2 pt-3">
                    <span class="font-weight-bold">Destinacije:</span>
                  </v-card-subtitle>
                  <v-card-text class="py-0">
                    <v-row dense>
                      <v-col
                        v-for="(ou, indexOu) in note.destinations"
                        :key="indexOu"
                        cols="6"
                        sm="4"
                        md="3">
                        <v-checkbox
                          v-model="ou.isSelected"
                          :label="ou.name"
                          hide-details
                          class="mt-0 touchscreen-checkbox">
                        </v-checkbox>
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <!-- Textarea za napomenu -->
                  <v-card-text class="pt-2 pb-3">
                    <v-textarea
                      v-model="note.text"
                      :label="`Napomena ${index + 1}`"
                      placeholder="Unesi napomenu za odabrane jedinice..."
                      rows="2"
                      auto-grow
                      outlined
                      dense
                      hide-details
                      class="touchscreen-input">
                    </v-textarea>
                  </v-card-text>
                </v-card>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- SEKCIJA 3: Povijest napomena (COLLAPSIBLE) -->
        <v-expansion-panels v-model="historyPanel">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-header">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-history</v-icon>
                <span class="font-weight-bold">Povijest napomena</span>
                <v-chip x-small class="ml-2">{{ previousNotes.length }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :items="previousNotes"
                :headers="headers"
                hide-default-footer
                :items-per-page="-1"
                dense
                class="history-table">
                <!-- Custom cell za napomenu - highlight stol notes -->
                <template #[`item.note.message`]="{ item }">
                  <div class="d-flex align-center">
                    <v-icon
                      v-if="isStolNote(item.note)"
                      small
                      color="#3989C9"
                      class="mr-2">
                      mdi-note-text
                    </v-icon>
                    <span>{{ item.note.message }}</span>
                  </div>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <!-- Footer actions -->
      <v-card-actions class="pa-4 border-top">
        <v-spacer></v-spacer>
        <v-btn
          class="stol-save-btn"
          large
          min-width="120"
          @click="saveAllNotes"
          :disabled="!hasAnyNotes">
          <v-icon left>mdi-content-save</v-icon>
          Spremi
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import state from '@/state'

export default {
  data () {
    return {
      dialogOpen: false,

      // Stol note
      stolNoteText: '',
      stolUnit: null, // Stol jedinica (učitana zasebno)

      // Unit notes
      notes: [],
      organizationUnits: [], // Sve ostale jedinice (bez stola)
      unitNotesPanel: null, // null = collapsed
      maxNotes: 3,

      // History
      previousNotes: [],
      historyPanel: null, // null = collapsed
      headers: [
        { text: 'Napomena', value: 'note.message', sortable: false },
        { text: 'Jedinica', value: 'note.unitName', sortable: false },
        { text: 'Dodao', value: 'order.added_by_name', sortable: false },
        { text: 'Status', value: 'order.order_status', sortable: false }
      ],

      // State
      currentTable: null,
      unitMessages: []
    }
  },

  computed: {
    minusDisabled () {
      return this.notes.length === 1 && this.notes[0].text === ''
    },

    plusDisabled () {
      if (this.notes.length >= this.maxNotes) return true
      const lastNote = this.notes[this.notes.length - 1]
      return !lastNote.text || !lastNote.destinations.some(d => d.isSelected)
    },

    activeUnitNotesCount () {
      return this.notes.filter(n =>
        n.text && n.destinations.some(d => d.isSelected)
      ).length
    },

    hasAnyNotes () {
      // Provjeri ima li stolne napomene
      const hasStolNote = this.stolNoteText && this.stolNoteText.trim() !== ''

      // Provjeri ima li napomena za org jedinice
      const hasUnitNotes = this.notes.some(note =>
        note.text && note.text.trim() !== '' && note.destinations.some(d => d.isSelected)
      )

      return hasStolNote || hasUnitNotes
    }
  },

  methods: {
    // Open dialog
    async open (table, options = {}) {
      this.currentTable = table
      const locationId = state.getPointOfSale().id

      // Reset sve podatke prije učitavanja da osiguramo fresh data
      this.organizationUnits = []
      this.stolUnit = null
      this.notes = []
      this.unitMessages = []

      await this.loadLocationUnits(locationId)
      await this.loadTableNotes(table.id)
      await this.loadNotesHistory(table.id)

      this.dialogOpen = true

      // Uvijek stavi focus na stol note input
      this.$nextTick(() => {
        this.$refs.stolNoteInput?.focus()
      })
    },

    // Detekcija stol jedinice
    isStolUnit (unit) {
      return unit.id === 'stol' || unit.name === 'Stol' || unit.type === 'table'
    },

    isStolNote (note) {
      return note.unitId === 'stol' || note.unitName === 'Stol' || note.isTableNote === true
    },

    getStolUnit () {
      return this.stolUnit
    },

    sortOrganizationUnits () {
      // Sortiraj organizacijske jedinice po sortOrder (stol jedinica nije ovdje)
      this.organizationUnits.sort((a, b) => {
        return (a.sortOrder || 0) - (b.sortOrder || 0)
      })
    },

    // Spremanje svih napomena (stol + org jedinice)
    async saveAllNotes () {
      // Resetiraj unitMessages i kreiraj nanovo
      this.unitMessages = []

      // Spremi stolnu napomenu ako postoji
      if (this.stolNoteText && this.stolNoteText.trim() !== '') {
        const stolUnit = this.getStolUnit()
        if (stolUnit) {
          this.unitMessages.push({
            unitId: stolUnit.id,
            unitName: stolUnit.name,
            message: this.stolNoteText,
            printer: stolUnit.printer || '',
            isTableNote: true
          })
        }
      }

      // Spremi napomene za org jedinice
      this.notes.forEach(note => {
        if (note.text && note.text.trim() !== '') {
          note.destinations.forEach(destination => {
            if (destination.isSelected) {
              const existingMessage = this.unitMessages.find(
                m => m.unitId === destination.id
              )

              if (existingMessage) {
                // Dodaj na postojeću poruku
                if (!existingMessage.message.includes(note.text)) {
                  existingMessage.message = existingMessage.message + ', ' + note.text
                }
              } else {
                // Kreiraj novu poruku
                this.unitMessages.push({
                  unitId: destination.id,
                  unitName: destination.name,
                  message: note.text,
                  printer: destination.printer || ''
                })
              }
            }
          })
        }
      })

      // Spremi u Firebase
      const saved = await this.saveTableNoteToFirebase()

      if (!saved) {
        return
      }

      // Emit event za parent komponente
      this.$emit('note-saved', this.unitMessages)

      // Zatvori dialog
      this.dialogOpen = false
    },

    // Spremanje u Firebase, returns true on success
    async saveTableNoteToFirebase () {
      if (!this.currentTable || !this.currentTable.id) return false

      const tableRef = df.doc(
        `location_orders/${state.getPointOfSale().id}/tables/${this.currentTable.id}`
      )

      try {
        const currentUser = state.getUser()
        await tableRef.set({
          notes: this.unitMessages,
          note_updated_at: Date.now(),
          note_updated_by: currentUser ? currentUser.displayName || currentUser.email || 'Nepoznato' : 'Nepoznato'
        }, { merge: true })
        return true
      } catch (error) {
        console.error('Error saving table note:', error)
        return false
      }
    },

    // Učitavanje postojećih napomena sa stola
    async loadTableNotes (tableId) {
      if (!tableId) {
        this.stolNoteText = ''
        this.unitMessages = []
        return
      }

      try {
        const tableDoc = await df.doc(
          `location_orders/${state.getPointOfSale().id}/tables/${tableId}`
        ).get()

        if (tableDoc.exists) {
          const data = tableDoc.data()
          this.unitMessages = data.notes || []

          // Popuni stol note text ako postoji
          const stolNote = this.unitMessages.find(n => this.isStolNote(n))
          this.stolNoteText = stolNote ? stolNote.message : ''
        }
      } catch (error) {
        console.error('Error loading table notes:', error)
      }
    },

    // Učitavanje povijesti
    async loadNotesHistory (tableId) {
      this.previousNotes = []

      if (!tableId) return

      try {
        const tableDoc = await df.doc(
          `location_orders/${state.getPointOfSale().id}/tables/${tableId}`
        ).get()

        if (tableDoc.exists) {
          const table = tableDoc.data()
          if (table.orders) {
            const orders = [...table.orders]
            orders.sort((a, b) =>
              (b.order_number || 0) - (a.order_number || 0)
            )

            orders.forEach(order => {
              if (order.notes && order.notes.length > 0) {
                order.notes.forEach(note => {
                  this.previousNotes.push({ note, order })
                })
              }
            })
          }
        }
      } catch (error) {
        console.error('Error loading notes history:', error)
      }
    },

    // Učitavanje ustrojstvenih jedinica s lazy creation of "stol" unit
    async loadLocationUnits (locationId) {
      try {
        // Check if stol unit exists, create if not (lazy creation)
        const stolUnitRef = df.doc(`location_units/${locationId}/units/stol`)
        const stolUnitDoc = await stolUnitRef.get()

        if (!stolUnitDoc.exists) {
          // Create it automatically
          await stolUnitRef.set({
            id: 'stol',
            name: 'Stol',
            type: 'table',
            isSystemUnit: true,
            sortOrder: -1,
            printer: '',
            printerName: '',
            created_at: Date.now()
          })
          // Load it back
          const newStolDoc = await stolUnitRef.get()
          this.stolUnit = newStolDoc.data()
        } else {
          // Load existing stol unit
          this.stolUnit = stolUnitDoc.data()
        }

        // Load all units (except Stol)
        const unitsSnapshot = await df.collection(
          `location_units/${locationId}/units`
        ).get()

        if (!unitsSnapshot.empty) {
          unitsSnapshot.forEach(doc => {
            const unit = doc.data()

            // Ne dodaj stol jedinicu u organizationUnits jer je ona gore posebno
            if (!this.isStolUnit(unit)) {
              unit.isSelected = false
              this.organizationUnits.push(unit)
            }
          })

          this.sortOrganizationUnits()
        }

        // Dodaj prvu praznu napomenu ako nema
        if (this.notes.length === 0) {
          this.addNote()
        }
      } catch (error) {
        console.error('Error loading org units:', error)
      }
    },

    // CRUD za unit notes
    addNote () {
      if (this.notes.length >= this.maxNotes) return

      this.notes.push({
        text: '',
        destinations: clone(this.organizationUnits)
      })
    },

    removeNote () {
      if (this.notes.length > 1) {
        this.notes.pop()
      } else {
        this.resetNote(0)
      }
    },

    resetNote (index) {
      this.notes[index].text = ''
      this.notes[index].destinations.forEach(d => {
        d.isSelected = false
      })
    },

    // Postojeća sendNotes metoda - zadržava kompatibilnost
    sendNotes (order) {
      // Process unit notes
      this.notes.forEach(note => {
        note.destinations.forEach(destination => {
          if (destination.isSelected) {
            const unitMessage = this.unitMessages.find(
              m => m.unitId === destination.id
            )

            if (unitMessage) {
              unitMessage.message = unitMessage.message + ', ' + note.text
            } else {
              this.unitMessages.push({
                unitId: destination.id,
                unitName: destination.name,
                message: note.text,
                printer: destination.printer
              })
            }
          }
        })
      })

      // Attach to order
      if (this.unitMessages.length > 0) {
        order.notes = this.unitMessages
      }
    },

    // Reset i close
    resetNotes () {
      this.stolNoteText = ''
      this.notes = []
      this.unitMessages = []
      this.addNote()
    },

    close () {
      // Validation prije zatvaranja
      const hasUnsavedUnitNotes = this.notes.some(note =>
        note.text && note.destinations.some(d => d.isSelected)
      )

      if (hasUnsavedUnitNotes) {
        // Upozori korisnika
        // TODO: Implement confirmation dialog
      }

      this.dialogOpen = false
    }
  }
}
</script>

<style scoped>
/* Dialog positioning */
.v-dialog__content >>> .add-note-dialog {
  position: absolute;
  top: 40px;
}

/* Dialog card */
.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* Header with Info Gradient */
.dialog-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  border-bottom: 1px solid rgba(57, 137, 201, 0.3);
  border-radius: 16px 16px 0 0 !important;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
}

.dialog-header .v-icon {
  position: relative;
  z-index: 1;
}

.header-text {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: white !important;
  letter-spacing: 0.3px !important;
  position: relative;
  z-index: 1;
}

.close-btn-header {
  position: relative;
  z-index: 1;
  transition: all 0.2s ease !important;
}

.close-btn-header:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Stolna napomena card - glass style */
.stol-note-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 4px 16px rgba(57, 137, 201, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.stol-note-card:hover {
  box-shadow: 0 6px 20px rgba(57, 137, 201, 0.18) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
}

.stol-note-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 50%,
    rgba(97, 84, 164, 0.08) 100%) !important;
  border-bottom: 1px solid rgba(57, 137, 201, 0.15);
  border-radius: 12px 12px 0 0 !important;
}

.stol-note-header .text-h6 {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #3989C9 !important;
  letter-spacing: 0.3px !important;
}

/* Expansion panels */
.v-expansion-panels {
  border-radius: 12px !important;
}

.v-expansion-panel {
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.12) !important;
  overflow: hidden;
}

.expansion-header {
  min-height: 56px !important;
  font-size: 1.05rem;
  background: rgba(255, 255, 255, 0.5) !important;
  transition: all 0.2s ease !important;
}

.expansion-header:hover {
  background: rgba(57, 137, 201, 0.05) !important;
}

.expansion-header .v-icon {
  color: #3989C9 !important;
}

.expansion-header .v-chip {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.15) 0%,
    rgba(57, 137, 201, 0.12) 100%) !important;
  color: #3989C9 !important;
  font-weight: 600 !important;
}

.v-expansion-panel-content {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* Touchscreen input optimizacije */
.touchscreen-input >>> .v-input__control {
  min-height: 52px;
}

.touchscreen-input >>> textarea {
  font-size: 16px !important;
  line-height: 1.5;
}

.touchscreen-input.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.touchscreen-input.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.touchscreen-input.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.touchscreen-input >>> label {
  color: rgba(57, 137, 201, 0.8) !important;
  font-weight: 500 !important;
}

/* Inner cards in expansion panels */
.v-expansion-panel-content >>> .v-card {
  background: rgba(255, 255, 255, 0.6) !important;
  border: 1px solid rgba(57, 137, 201, 0.12) !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.08) !important;
}

.v-expansion-panel-content >>> .v-card:hover {
  border-color: rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 3px 10px rgba(57, 137, 201, 0.12) !important;
}

/* Touchscreen checkbox optimizacije */
.touchscreen-checkbox >>> .v-input--selection-controls__input {
  width: 32px !important;
  height: 32px !important;
}

.touchscreen-checkbox >>> .v-icon {
  font-size: 28px !important;
  color: #3989C9 !important;
}

.touchscreen-checkbox >>> .v-input--is-label-active .v-icon,
.touchscreen-checkbox >>> .v-input--is-dirty .v-icon {
  color: #3989C9 !important;
}

/* History table */
.history-table >>> .v-data-table__wrapper {
  max-height: 300px;
  overflow-y: auto;
}

.history-table >>> tbody tr {
  transition: background 0.2s ease !important;
}

.history-table >>> tbody tr:hover {
  background: rgba(57, 137, 201, 0.05) !important;
}

.history-table >>> th {
  color: #3989C9 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}

.history-table >>> td {
  font-size: 14px !important;
  color: rgba(70, 70, 80, 0.9) !important;
}

/* Border top za footer */
.border-top {
  border-top: 1px solid rgba(57, 137, 201, 0.15);
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.03) 0%,
    rgba(57, 137, 201, 0.02) 50%,
    rgba(97, 84, 164, 0.03) 100%) !important;
  border-radius: 0 0 16px 16px !important;
}

/* Stol Save Button - Soft style */
.stol-save-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 44px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.stol-save-btn:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}

.stol-save-btn:disabled {
  opacity: 0.5 !important;
  background: rgba(245, 245, 245, 0.8) !important;
  color: rgba(158, 158, 158, 0.6) !important;
  border-color: rgba(200, 200, 200, 0.3) !important;
}

.stol-save-btn .v-icon {
  font-size: 20px !important;
  color: #3989C9 !important;
}

.stol-save-btn:disabled .v-icon {
  color: rgba(158, 158, 158, 0.6) !important;
}

/* Button spacing - minimum 44px za touchscreen */
.v-btn {
  min-height: 44px !important;
}

.v-btn.v-size--large {
  min-height: 52px !important;
  padding: 0 24px !important;
}

.v-btn.v-btn--icon.v-size--large {
  min-width: 52px !important;
  min-height: 52px !important;
}

/* Icon buttons in expansion content */
.v-expansion-panel-content .v-btn--icon {
  transition: all 0.2s ease !important;
}

.v-expansion-panel-content .v-btn--icon:hover {
  transform: scale(1.05);
}

/* Scrollbar styling */
.v-card-text::-webkit-scrollbar {
  width: 8px;
}

.v-card-text::-webkit-scrollbar-track {
  background: rgba(57, 137, 201, 0.05);
  border-radius: 4px;
}

.v-card-text::-webkit-scrollbar-thumb {
  background: rgba(57, 137, 201, 0.3);
  border-radius: 4px;
}

.v-card-text::-webkit-scrollbar-thumb:hover {
  background: rgba(57, 137, 201, 0.5);
}

/* Card subtitle styling */
.v-card-subtitle {
  color: #3989C9 !important;
  font-weight: 600 !important;
}

/* Text caption styling */
.text-caption.grey--text {
  color: rgba(57, 137, 201, 0.7) !important;
  font-weight: 500 !important;
  letter-spacing: 0.2px !important;
}
</style>
